import { useCallback, useEffect, useState } from "react";

import {
  GET_DEPARTMENT_LIST,
  GET_DESIGNATION_LIST,
  GET_ALL_EMPLOYEES_NAME,
} from "../../../../../config/Endpoints.js";
import { getApi } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import type {
  DepartmentRow,
  DesignationRow,
  EmployeeOption,
  EmployeeRow,
} from "../types.ts";
import {
  extractApiMessage,
  toEmployeeOptions,
  unwrapApiDataArray,
} from "../utils.ts";

export function useEmployeeProgressionData() {
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
  const [departments, setDepartments] = useState<DepartmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBaseData = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const [employeeResponse, departmentResponse] = await Promise.all([
        getApi<unknown>(GET_ALL_EMPLOYEES_NAME, accessToken(), "json", signal),
        getApi<unknown>(GET_DEPARTMENT_LIST, accessToken(), "json", signal),
      ]);

      const employeeRows = unwrapApiDataArray(employeeResponse.data) as EmployeeRow[];
      setEmployees(employeeRows);
      setEmployeeOptions(toEmployeeOptions(employeeRows));
      setDepartments(unwrapApiDataArray(departmentResponse.data) as DepartmentRow[]);
    } catch (e) {
      setError(extractApiMessage(e, "Failed to load employee progression data."));
      setEmployees([]);
      setEmployeeOptions([]);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDesignations = useCallback(async (departmentId: string) => {
    if (!departmentId) return [];
    const { data } = await getApi<unknown>(
      `${GET_DESIGNATION_LIST}?departmentId=${departmentId}`,
      accessToken(),
    );
    return unwrapApiDataArray(data) as DesignationRow[];
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void fetchBaseData(ac.signal);
    return () => ac.abort();
  }, [fetchBaseData]);

  return {
    employees,
    employeeOptions,
    departments,
    loading,
    error,
    refetch: fetchBaseData,
    fetchDesignations,
  };
}

